import { call, put, takeEvery, all, select } from 'redux-saga/effects';
import { Routine } from 'redux-saga-routines';
import LogRocket from 'logrocket';
import {
  getReferenceWithQuestions,
  startSurvey,
  finishSurvey,
  finishSurveyManually,
  startSurveyManually,
  confirmReference,
  declineSurvey,
  checkKey,
  checkCode,
  confirmRefManualInput
} from '../../../services/referenceService';
import {
  fetchReferenceSurveyRoutine,
  fetchQuestionsRoutine,
  startSurveyRoutine,
  confirmReferenceRoutine,
  answerQuestionRoutine,
  finishSurveyRoutine,
  declineSurveyRoutine,
  createProspectRoutine,
  checkSurveyKeyRoutine,
  checkSurveyCodeRoutine
} from '../routines';
import {
  saveAnswer,
  fetchQuestions,
  fetchQuestionsForManualInput,
  saveManualInputAnswer
} from '../../../services/referenceQuestionService';
import { createProspect, createProspectManually } from '../../../services/prospectsService';
import { setWorkspaceDataRoutine } from '../../../routines/workspace';
import { setCode } from '../../../common/helpers/common';
import { IReferenceWithQuestions } from '../../../common/models/reference/IReferenceWithQuestions';
import { IAppState } from '../../../common/models/store/IAppState';

function* identifyReference({ reference }: IReferenceWithQuestions) {
  const workspaceName = yield select(({ workspace }: IAppState) => workspace.name);
  const name = `${reference.firstName} ${reference.lastName}`;
  const { contactId: id } = reference;

  LogRocket.identify(id, {
    name,
    email: `${workspaceName} - Reference`,
    contactEmail: reference.email
  });
  heap.identify(id);
  heap.addUserProperties({ name, workspace: workspaceName });
}

function* getReferenceWithQuestionsRequest() {
  try {
    const { workspace, ...response } = yield call(getReferenceWithQuestions);
    yield put(setWorkspaceDataRoutine(workspace));
    yield put(fetchReferenceSurveyRoutine.success(response));
    yield identifyReference(response);
  } catch (error) {
    yield put(fetchReferenceSurveyRoutine.failure(error));
  }
}

function* checkSurveyCodeRequest({ payload }: Routine<any>) {
  try {
    const response = yield call(checkCode, payload);
    setCode(payload);
    yield put(checkSurveyCodeRoutine.success(response));
    yield identifyReference(response);
  } catch (error) {
    yield put(checkSurveyCodeRoutine.failure(error.message));
  }
}

function* checkSurveyKeyRequest() {
  try {
    const response = yield call(checkKey);
    yield put(setWorkspaceDataRoutine.trigger(response));
    yield put(checkSurveyKeyRoutine.success());
  } catch (error) {
    yield put(checkSurveyKeyRoutine.failure(error.message));
  }
}

function* fetchQuestionsRequest({ payload }: Routine<any>) {
  try {
    const response = yield call(payload.isManualInput ? fetchQuestionsForManualInput : fetchQuestions, payload.data);

    yield put(fetchQuestionsRoutine.success(response));
  } catch {
    yield put(fetchQuestionsRoutine.failure());
  }
}

function* confirmReferenceRequest({ payload }: Routine<any>) {
  try {
    const response = yield call(payload.isManualInput ? confirmRefManualInput : confirmReference, payload.data);

    yield put(confirmReferenceRoutine.success({ reference: payload.data, questions: response }));
  } catch {
    yield put(confirmReferenceRoutine.failure());
  }
}

function* answerQuestionRequest({ payload }: Routine<any>) {
  try {
    const { question, finish, isManualInput } = payload;
    const response = yield call(isManualInput ? saveManualInputAnswer : saveAnswer, question);
    yield put(answerQuestionRoutine.success(response));

    if (finish) {
      yield put(finishSurveyRoutine.trigger({ isManualInput, id: question.referenceId }));
    }
  } catch {
    yield put(answerQuestionRoutine.failure());
  }
}

function* startSurveyRequest({ payload }: Routine<any>) {
  try {
    const response = yield call(payload.isManualInput ? startSurveyManually : startSurvey, payload.id);

    yield put(startSurveyRoutine.success(response));
  } catch {
    yield put(startSurveyRoutine.failure());
  }
}

function* finishSurveyRequest({ payload }: Routine<any>) {
  try {
    const response = yield call(payload.isManualInput ? finishSurveyManually : finishSurvey, payload.id);

    yield put(finishSurveyRoutine.success(response));
  } catch {
    yield put(finishSurveyRoutine.failure());
  }
}

function* declineSurveyRequest({ payload }: Routine<any>) {
  try {
    yield call(declineSurvey, payload);
    yield put(declineSurveyRoutine.success());
    yield put(finishSurveyRoutine.trigger({ isManualInput: false }));
  } catch {
    yield put(declineSurveyRoutine.failure());
  }
}

function* createProspectRequest({ payload }: Routine<any>) {
  try {
    yield call(payload.isManualInput ? createProspectManually : createProspect, payload.data);
    yield put(createProspectRoutine.success());
  } catch {
    yield put(createProspectRoutine.failure());
  }
}

function* watchGetReferenceWithQuestionsRequest() {
  yield takeEvery(fetchReferenceSurveyRoutine.TRIGGER, getReferenceWithQuestionsRequest);
}

function* watchFetchQuestionsRequest() {
  yield takeEvery(fetchQuestionsRoutine.TRIGGER, fetchQuestionsRequest);
}

function* watchConfirmReferenceRequest() {
  yield takeEvery(confirmReferenceRoutine.TRIGGER, confirmReferenceRequest);
}

function* watchAnswerQuestionRoutineRequest() {
  yield takeEvery(answerQuestionRoutine.TRIGGER, answerQuestionRequest);
}

function* watchStartSurveyRequest() {
  yield takeEvery(startSurveyRoutine.TRIGGER, startSurveyRequest);
}

function* watchFinishSurveyRequest() {
  yield takeEvery(finishSurveyRoutine.TRIGGER, finishSurveyRequest);
}

function* watchDeclineSurveyRequest() {
  yield takeEvery(declineSurveyRoutine.TRIGGER, declineSurveyRequest);
}

function* watchCreateProspectRequest() {
  yield takeEvery(createProspectRoutine.TRIGGER, createProspectRequest);
}

function* watchCheckSurveyKeyRequest() {
  yield takeEvery(checkSurveyKeyRoutine.TRIGGER, checkSurveyKeyRequest);
}

function* watchCheckSurveyCodeRequest() {
  yield takeEvery(checkSurveyCodeRoutine.TRIGGER, checkSurveyCodeRequest);
}

export default function* refSurveySaga() {
  yield all([
    watchGetReferenceWithQuestionsRequest(),
    watchConfirmReferenceRequest(),
    watchStartSurveyRequest(),
    watchFinishSurveyRequest(),
    watchAnswerQuestionRoutineRequest(),
    watchFetchQuestionsRequest(),
    watchDeclineSurveyRequest(),
    watchCreateProspectRequest(),
    watchCheckSurveyKeyRequest(),
    watchCheckSurveyCodeRequest()
  ]);
}
